/**
 * Reusable public newsletter subscription form.
 * Saves subscriber to Firestore; CF-1 (onNewsletterSubscriptionCreated) sends the welcome email automatically.
 * Root admin can also sync to Brevo from Admin → Newsletter Subscribers.
 * Usage: <NewsletterSubscribeForm source="footer_newsletter" />
 */

import { useRef, useState } from "react";
import { Loader2, CheckCircle2, Mail, ArrowRight } from "lucide-react";
import { subscribeToNewsletter } from "@/lib/newsletter";

interface NewsletterSubscribeFormProps {
	/** Identifies where this form was embedded (stored on subscription record). */
	source: string;
	/** Override the form heading. */
	heading?: string;
	/** Override the form description. */
	description?: string;
	/** Override the submit button label. */
	buttonLabel?: string;
	/** Additional CSS classes on the root element. */
	className?: string;
	/** Visual variant: 'default' renders a card-style form; 'inline' renders a compact horizontal form. */
	variant?: "default" | "inline";
	/** Optional full class string for the submit button (e.g. Hope Gold CTA on Mission Blue). */
	submitButtonClassName?: string;
}

type FormState = "idle" | "submitting" | "success" | "already_subscribed";

/** Validate email format */
function isValidEmail(email: string): boolean {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export function NewsletterSubscribeForm({
	source,
	heading: _heading = "Stay in the loop",
	description: _description = "Get government contracting insights, tips, and updates delivered to your inbox.",
	buttonLabel = "Subscribe",
	className = "",
	variant = "default",
	submitButtonClassName,
}: NewsletterSubscribeFormProps): JSX.Element {
	const [email, setEmail] = useState("");
	const [firstName, setFirstName] = useState("");
	const [formState, setFormState] = useState<FormState>("idle");
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	// Anti-spam: honeypot field (should always stay empty for real users)
	const [honeypot, setHoneypot] = useState("");
	// Anti-spam: timestamp when the form first rendered
	const mountTime = useRef(Date.now());
	// Anti-spam: localStorage rate limit (2 min between submissions per browser)
	const COOLDOWN_KEY = "gsgo_nl_last_ts";
	const COOLDOWN_MS = 120_000;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setErrorMessage(null);

		// Layer 1 — Honeypot: bot filled the hidden field, silently succeed.
		if (honeypot.trim()) {
			setFormState("success");
			setEmail("");
			setFirstName("");
			return;
		}

		// Layer 2 — Timing check: reject sub-1.5s submissions (bots submit instantly).
		if (Date.now() - mountTime.current < 1500) {
			setErrorMessage("Please review your inputs and try again.");
			return;
		}

		// Layer 3 — localStorage rate limit: prevent rapid resubmission from same browser.
		try {
			const last = localStorage.getItem(COOLDOWN_KEY);
			if (last && Date.now() - Number(last) < COOLDOWN_MS) {
				setFormState("success");
				setEmail("");
				setFirstName("");
				return;
			}
		} catch {
			// localStorage unavailable (private browsing) — allow through.
		}

		// Layer 4 — Max length validation (belt + suspenders beyond maxLength attr).
		if (email.trim().length > 254) {
			setErrorMessage("The email address is too long. Please enter a valid email.");
			return;
		}
		if (firstName.trim().length > 100) {
			setErrorMessage("First name is too long.");
			return;
		}

		if (!isValidEmail(email)) {
			setErrorMessage("Please enter a valid email address.");
			return;
		}

		setFormState("submitting");

		try {
			const result = await subscribeToNewsletter(
				email,
				firstName || null,
				source,
			);

			if (result === "already_subscribed") {
				setFormState("already_subscribed");
				return;
			}

			setFormState("success");
			setEmail("");
			setFirstName("");

			// Record cooldown so the same browser can't spam the form.
			try {
				localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
			} catch {
				// noop
			}
		} catch (err) {
			console.error("[NewsletterSubscribeForm] Subscription error:", err);
			setErrorMessage("Something went wrong. Please try again.");
			setFormState("idle");
		}
	};

	if (formState === "success") {
		return (
			<div
				className={`flex flex-col items-center justify-center text-center py-6 px-4 ${className}`}
			>
				<div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
					<CheckCircle2 className="text-emerald-600" size={24} />
				</div>
				<p className="font-semibold text-gray-900 text-lg">
					You're subscribed!
				</p>
				<p className="text-gray-500 text-sm mt-1">
					Thanks for signing up. You’re on the list.
				</p>
				<p className="text-gray-400 text-xs mt-2">
					Check your inbox for a welcome email from us—it should arrive shortly.
				</p>
			</div>
		);
	}

	if (formState === "already_subscribed") {
		return (
			<div
				className={`flex flex-col items-center justify-center text-center py-6 px-4 ${className}`}
			>
				<div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
					<Mail className="text-blue-600" size={24} />
				</div>
				<p className="font-semibold text-gray-900 text-lg">
					Already subscribed
				</p>
				<p className="text-gray-500 text-sm mt-1">
					This email is already on our list. We'll keep you posted!
				</p>
			</div>
		);
	}

	if (variant === "inline") {
		return (
			<form
				onSubmit={handleSubmit}
				className={`w-full ${className}`}
				noValidate
			>
				{/* Honeypot — visually hidden; bots fill it, real users don't */}
				<div
					aria-hidden="true"
					style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden", opacity: 0 }}
				>
					<input
						type="text"
						name="full_name"
						value={honeypot}
						onChange={(e) => setHoneypot(e.target.value)}
						tabIndex={-1}
						autoComplete="off"
					/>
				</div>
				<div className="flex flex-col sm:flex-row gap-3">
					<input
						type="text"
						value={firstName}
						onChange={(e) => setFirstName(e.target.value)}
						placeholder="First name (optional)"
						maxLength={100}
						className="flex-none sm:w-56 sm:min-w-[11rem] rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
						disabled={formState === "submitting"}
					/>
					<input
						type="email"
						value={email}
						onChange={(e) => {
							setEmail(e.target.value);
							setErrorMessage(null);
						}}
						placeholder="Your email address"
						required
						maxLength={254}
						className={`flex-1 min-w-0 rounded-lg border px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-1 ${
							errorMessage
								? "border-red-400 focus:border-red-500 focus:ring-red-500"
								: "border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-500"
						}`}
						disabled={formState === "submitting"}
					/>
					<button
						type="submit"
						disabled={formState === "submitting"}
						className={
							submitButtonClassName ??
							"flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						}
					>
						{formState === "submitting" ? (
							<Loader2 size={16} className="animate-spin" />
						) : (
							<>
								<span>{buttonLabel}</span>
								<ArrowRight size={15} />
							</>
						)}
					</button>
				</div>
				{errorMessage && (
					<p className="mt-2 text-xs text-red-600">{errorMessage}</p>
				)}
			</form>
		);
	}

	// Default card variant
	return (
		<form onSubmit={handleSubmit} className={`w-full ${className}`} noValidate>
			{/* Honeypot — visually hidden; bots fill it, real users don't */}
			<div
				aria-hidden="true"
				style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden", opacity: 0 }}
			>
				<input
					type="text"
					name="full_name"
					value={honeypot}
					onChange={(e) => setHoneypot(e.target.value)}
					tabIndex={-1}
					autoComplete="off"
				/>
			</div>
			<div className="flex flex-col gap-3">
				<div className="flex flex-col sm:flex-row gap-3">
					<input
						type="text"
						value={firstName}
						onChange={(e) => setFirstName(e.target.value)}
						placeholder="First name (optional)"
						maxLength={100}
						className="flex-none sm:w-56 sm:min-w-[11rem] rounded-lg border border-white/30 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/60 focus:border-white/60 focus:outline-none focus:ring-1 focus:ring-white/60 backdrop-blur-sm"
						disabled={formState === "submitting"}
					/>
					<input
						type="email"
						value={email}
						onChange={(e) => {
							setEmail(e.target.value);
							setErrorMessage(null);
						}}
						placeholder="Enter your email address"
						required
						maxLength={254}
						className={`flex-1 min-w-0 rounded-lg border px-4 py-3 text-sm placeholder:text-white/60 focus:outline-none focus:ring-1 backdrop-blur-sm ${
							errorMessage
								? "border-red-400 bg-white/10 text-white focus:border-red-300 focus:ring-red-400"
								: "border-white/30 bg-white/10 text-white focus:border-white/60 focus:ring-white/60"
						}`}
						disabled={formState === "submitting"}
					/>
				</div>
				<button
					type="submit"
					disabled={formState === "submitting"}
					className={
						submitButtonClassName ??
						"flex items-center justify-center gap-2 w-full rounded-lg bg-white px-5 py-3 text-sm font-semibold text-blue-700 shadow-md hover:bg-blue-50 active:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
					}
				>
					{formState === "submitting" ? (
						<>
							<Loader2 size={16} className="animate-spin" />
							<span>Subscribing…</span>
						</>
					) : (
						<>
							<span>{buttonLabel}</span>
							<ArrowRight size={15} />
						</>
					)}
				</button>
				{errorMessage && (
					<p className="text-xs text-red-300 text-center">{errorMessage}</p>
				)}
			</div>
		</form>
	);
}
